import React, { useRef } from 'react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { i18n } from '@/locale/i18n'
import { questionDetailUrl, keepQueryString } from '@/urls'
import useScoreChangeBurst from '@/components/ui/confetti/useScoreChangeBurst'
import SportIcon from './SportIcon'
import RowBetCountToggle from '@/features/market/market-compact-row/RowBetCountToggle'
import MarketVolumeStat from '@/features/market/question-footer/MarketVolumeStat'
import {
  getMainSportsScores,
  getMatchMinute,
  isSportsMarketResolved,
  isSportsMatchLive,
  getSportsKind,
  shouldShowSportsScore,
} from '@/features/market/utils/sportsEvent'

/**
 * One fixture: kickoff (or the live minute) on the left; in the middle the sport and
 * championship over the match name; volume and the sides' odds on the right.
 */
const SportEventCompactRow = ({
  question,
  outcomes,
  isRealMoney = false,
  betListOpen = false,
  onToggleBetList,
}) => {
  const sportsData = question?.event_extra_data || {}
  const { icon: sportIcon, sportLabel, league } = getSportsKind(question)
  const eyebrow = [sportLabel, league].filter(Boolean).join(' · ')
  const scoreRef = useRef(null)

  const isResolved = isSportsMarketResolved(question)
  const isLive = isSportsMatchLive(question) && !isResolved
  const showingScore = shouldShowSportsScore(question, sportsData)
  const scores = getMainSportsScores(sportsData)
  const matchMinute = isLive ? getMatchMinute(sportsData) : null

  useScoreChangeBurst(scoreRef, {
    scores: {
      homeRegular: sportsData.home_score_regular_time,
      awayRegular: sportsData.away_score_regular_time,
      homeExtraTime: sportsData.home_score_extra_time,
      awayExtraTime: sportsData.away_score_extra_time,
      homePenalties: sportsData.home_score_penalties,
      awayPenalties: sportsData.away_score_penalties,
    },
    enabled: isLive,
  })

  const kickoff = question?.event_start_date ? dayjs(question.event_start_date) : null

  const renderTimeColumn = () => {
    if (isLive) {
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="relative flex h-1.5 w-1.5" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-negative-4 opacity-60 [animation-duration:1.8s]" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-negative-4" />
          </span>
          <span className="text-xs font-semibold tabular-nums text-negative-4">
            {matchMinute || i18n('question_card.sport_event.live')}
          </span>
        </div>
      )
    }

    if (isResolved) {
      return <span className="text-xs font-medium uppercase text-Neutral-4">{i18n('question_card.sport_event.full_time')}</span>
    }

    if (!kickoff) return null

    /* The time alone says nothing about what it is the time of, and the column header
       that does is decoration a screen reader never reaches. */
    return (
      <>
        <span className="sr-only">
          {i18n('markets.kickoff_at_label', { date: kickoff.format('DD MMM YYYY, HH:mm') })}
        </span>
        <div aria-hidden className="text-sm font-semibold tabular-nums text-Neutral-6">{kickoff.format('HH:mm')}</div>
        <div aria-hidden className="text-xs text-Neutral-4">
          {kickoff.isSame(dayjs(), 'day') ? i18n('common.today') : kickoff.format('DD MMM')}
        </div>
      </>
    )
  }

  const sideName = (code, name) => name || code || 'TBD'
  const homeName = sideName(sportsData.home_team_short_code, sportsData.home_team)
  const awayName = sideName(sportsData.away_team_short_code, sportsData.away_team)

  return (
    <div className="flex flex-nowrap items-center gap-x-4 px-4 py-3">
      <div className="flex w-14 shrink-0 flex-col items-center justify-center text-center">{renderTimeColumn()}</div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {eyebrow && (
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-Neutral-4">
            <SportIcon icon={sportIcon} />
            <span className="truncate" title={eyebrow}>
              {eyebrow}
            </span>
          </span>
        )}

        {/* The match name is the row's way to the market page, and its only keyboard
            stop: clicking the rest of the row aims the bet pad instead. The card's click
            handler stands down for nested anchors, so the two never both fire. While the
            match is on, the score sits between the two names. */}
        <Link
          to={keepQueryString(questionDetailUrl(question))}
          className="question-card__row-title min-w-0 truncate text-sm font-bold text-Neutral-6">
          {homeName}
          {showingScore ? (
            <span ref={scoreRef} className="tabular-nums">
              {` ${scores.home ?? 0} – ${scores.away ?? 0} `}
            </span>
          ) : (
            ` ${i18n('question_card.sport_event.versus')} `
          )}
          {awayName}
        </Link>

        {/* Under the match name, where the market row keeps it under its title: a control,
            since it opens the reader's positions on this fixture. Renders nothing when
            they hold none. */}
        <RowBetCountToggle question={question} open={betListOpen} onToggle={onToggleBetList} />
      </div>

      {/* Its own column, under the header that names it, so the figures line up down the
          list instead of trailing each fixture, at the same width and inset the market
          row gives it. */}
      <div className="flex w-32 shrink-0 items-center justify-start pl-8">
        <MarketVolumeStat question={question} isRealMoney={isRealMoney} />
      </div>

      {outcomes && <div className="shrink-0">{outcomes}</div>}
    </div>
  )
}

export default SportEventCompactRow
